import React from "react";
import styled from "styled-components";
import { brand_orange_deep, outerContainerCss } from "../../constants/styles";

const STATUS_LABELS = {
  ineligible: { label: "Belum Memenuhi Syarat", tone: "muted" },
  eligible: { label: "Sertifikat Siap Diklaim", tone: "success" },
  claimed: { label: "Sertifikat Diterbitkan", tone: "success" },
  expired: { label: "Periode Klaim Berakhir", tone: "danger" },
};

function CourseCertificate({ course }) {
  const status = course?.certificate_status;
  if (!status) return null;
  const meta = STATUS_LABELS[status] || STATUS_LABELS.ineligible;
  const breakdown = course.certificate_breakdown || [];
  const pct =
    typeof course.course_progress_percent === "number"
      ? Math.max(0, Math.min(100, course.course_progress_percent))
      : null;

  const curriculums = course.curriculums || [];
  const totalModules = curriculums.length;
  const modulesCompleted = curriculums.filter(
    (c) => (c.progress_percent || 0) >= 100,
  ).length;
  const examsAvailable = curriculums.filter((c) => c.exam_available).length;
  const examsPassed = curriculums.filter(
    (c) => c.exam_status === "passed",
  ).length;
  const claimed = status === "claimed";

  const milestones = [];
  if (totalModules > 0) {
    milestones.push({
      label: `Selesaikan ${totalModules} Modul`,
      meta: `${modulesCompleted} / ${totalModules} selesai`,
      done: modulesCompleted >= totalModules,
    });
  }
  if (examsAvailable > 0) {
    milestones.push({
      label: `Lulus ${examsAvailable} Ujian`,
      meta: `${examsPassed} / ${examsAvailable} lulus`,
      done: examsPassed >= examsAvailable,
    });
  }
  milestones.push({
    label: "Klaim Sertifikat",
    meta: claimed
      ? "Sertifikat sudah diterbitkan"
      : "Setelah modul & ujian selesai",
    done: claimed,
  });

  return (
    <Section aria-labelledby="course-certificate">
      <Inner>
        <Card>
          <Body>
            <Eyebrow $tone={meta.tone}>{meta.label}</Eyebrow>
            <Heading id="course-certificate">Sertifikat {course.name}</Heading>
            <Milestones>
              {milestones.map((m, i) => (
                <Milestone key={m.label} $done={m.done}>
                  <MilestoneIcon $done={m.done} aria-hidden="true">
                    {m.done ? (
                      <svg viewBox="0 0 16 16" width="14" height="14">
                        <path
                          d="M3 8.5l3 3 7-7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </MilestoneIcon>
                  <MilestoneBody>
                    <MilestoneLabel $done={m.done}>{m.label}</MilestoneLabel>
                    <MilestoneMeta>{m.meta}</MilestoneMeta>
                  </MilestoneBody>
                </Milestone>
              ))}
            </Milestones>
            {breakdown.length > 0 && (
              <Breakdown>
                {breakdown.slice(0, 6).map((line, i) => (
                  // Position is the identity: static prose lines, no per-row
                  // state, never reordered, and duplicate lines are possible.
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <BreakdownItem key={i}>{line}</BreakdownItem>
                ))}
              </Breakdown>
            )}
            {(status === "eligible" ||
              (status === "claimed" && course.certificate_url) ||
              status === "expired") && (
              <CtaRow>
                {status === "eligible" && (
                  <PrimaryButton href="/dashboards" type="button">
                    Klaim Sertifikat →
                  </PrimaryButton>
                )}
                {status === "claimed" && course.certificate_url && (
                  <PrimaryButton
                    href={course.certificate_url}
                    target="_blank"
                    rel="noopener"
                  >
                    Unduh Sertifikat ↓
                  </PrimaryButton>
                )}
                {status === "expired" && (
                  <Hint>
                    Periode klaim sertifikat untuk Anda telah berakhir.
                  </Hint>
                )}
              </CtaRow>
            )}
          </Body>
          {pct !== null && (
            <DialWrap aria-hidden="true">
              <DialBadge>
                <DialBadgeIcon viewBox="0 0 24 24">
                  <path
                    d="M12 2l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 15.5l-5.2 2.6 1-5.8-4.3-4.1 5.9-.9L12 2z"
                    fill="currentColor"
                  />
                </DialBadgeIcon>
              </DialBadge>
              <ProgressDial percent={pct} />
              <DialLabel>Progres total</DialLabel>
            </DialWrap>
          )}
        </Card>
      </Inner>
    </Section>
  );
}

function ProgressDial({ percent }) {
  const size = 132;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (c * percent) / 100;
  return (
    <DialSvg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <defs>
        <linearGradient id="dial-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#D85907" />
          <stop offset="100%" stopColor="#F7B30C" />
        </linearGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(15, 23, 42, 0.08)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="url(#dial-grad)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="26"
        fontWeight="800"
        fill={brand_orange_deep}
        fontFamily="Montserrat, sans-serif"
      >
        {`${percent}%`}
      </text>
    </DialSvg>
  );
}

export default React.memo(CourseCertificate);

const Section = styled.section`
  background: #ffffff;
  padding: 48px 0 24px;
  [data-theme="dark"] & {
    background: #0f172a;
  }

  @media (max-width: 1023px) {
    padding: 32px 0 16px;
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
`;

const Card = styled.div`
  position: relative;
  background:
    radial-gradient(
      140% 90% at 100% 0%,
      rgba(247, 179, 12, 0.18),
      transparent 55%
    ),
    linear-gradient(135deg, #fff7ed, #ffedd5);
  border: 1px solid #fed7aa;
  border-radius: 20px;
  padding: 32px 36px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 40px;
  align-items: center;
  overflow: hidden;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 24px 22px;
  }

  [data-theme="dark"] & {
    background:
      radial-gradient(
        140% 90% at 100% 0%,
        rgba(247, 179, 12, 0.18),
        transparent 55%
      ),
      rgba(216, 89, 7, 0.08);
    border-color: rgba(216, 89, 7, 0.32);
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const DialWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  justify-self: end;
  padding: 4px;

  @media (max-width: 1023px) {
    justify-self: center;
  }
`;

const DialBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f7b30c, #d85907);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(216, 89, 7, 0.32);
  z-index: 1;
`;

const DialBadgeIcon = styled.svg`
  width: 18px;
  height: 18px;
`;

const DialSvg = styled.svg`
  width: 132px;
  height: 132px;
`;

const DialLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $tone }) =>
    $tone === "success"
      ? "#047857"
      : $tone === "danger"
        ? "#b91c1c"
        : "#4b5563"};
  background: ${({ $tone }) =>
    $tone === "success"
      ? "rgba(16,185,129,0.16)"
      : $tone === "danger"
        ? "rgba(220,38,38,0.16)"
        : "rgba(107,114,128,0.16)"};
`;

const Heading = styled.h2`
  font-size: 26px;
  font-weight: 800;
  margin: 0 0 4px;
  color: ${brand_orange_deep};
  line-height: 1.25;
`;

const Milestones = styled.ol`
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Milestone = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ $done }) =>
    $done ? "rgba(16, 185, 129, 0.10)" : "rgba(255, 255, 255, 0.55)"};
  border: 1px solid
    ${({ $done }) =>
      $done ? "rgba(16, 185, 129, 0.32)" : "rgba(216, 89, 7, 0.20)"};

  [data-theme="dark"] & {
    background: ${({ $done }) =>
      $done ? "rgba(16, 185, 129, 0.18)" : "rgba(255, 255, 255, 0.04)"};
    border-color: ${({ $done }) =>
      $done ? "rgba(16, 185, 129, 0.45)" : "rgba(255, 255, 255, 0.10)"};
  }
`;

const MilestoneIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ $done }) => ($done ? "#10b981" : "rgba(216, 89, 7, 0.18)")};
  color: ${({ $done }) => ($done ? "#fff" : brand_orange_deep)};
  font-weight: 800;
  font-size: 13px;

  [data-theme="dark"] & {
    color: ${({ $done }) => ($done ? "#fff" : "#fdba74")};
  }
`;

const MilestoneBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const MilestoneLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $done }) => ($done ? "#047857" : "#1f2937")};

  [data-theme="dark"] & {
    color: ${({ $done }) => ($done ? "#34d399" : "#f3f4f6")};
  }
`;

const MilestoneMeta = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Breakdown = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #4b5563;
  [data-theme="dark"] & {
    color: #cbd5e1;
  }
`;

const BreakdownItem = styled.li`
  &::before {
    content: "• ";
    color: ${brand_orange_deep};
    font-weight: 800;
  }
`;

const CtaRow = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-block;
  padding: 12px 26px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &:hover {
    filter: brightness(0.95);
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;
